import { ClinicalCompanyConfiguration } from '../../../components/ClinicalCompanyConfiguration';
import { DerivedVitalsPanel } from '../../../components/DerivedVitalsPanel';
import { ageInYears, derivedFromCodes } from '@dru/clinical-math/derived-vitals';
import { clinicalSummary } from '@dru/clinical-math/clinical-summary';
import { TOOTH_CHART_CODE, formatToothChart } from '@dru/clinical-math/dental';
import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { formatDate, formatDateTime, formatTime } from '../../../lib/format';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  AlertTriangle, ArrowDown, ChevronDown, ChevronLeft, ClipboardCheck, Download, Eye, FileCheck2, FilePenLine, FileText, IdCard,
  History, Lock, ShieldAlert, Siren,
} from 'lucide-react';
import type {
  ClinicalResult, ClinicalView, CorrectionType, CursorPage, FitnessDecision, HealthReportSummary, LabReportSummary,
  RestrictionCodeRow,
} from '@dru/types';
import {
  CONSENT_METHOD_LABEL, CORRECTION_OUTCOME_LABEL, CORRECTION_STATUS_LABEL, CORRECTION_TYPE_LABEL, EXAM_TYPE_LABEL,
  FITNESS_LABEL, FLAG_LABEL,
  LAB_REPORT_STATUS_LABEL, STATION_LABEL,
} from '@dru/types';
import { openReport } from '../../lab/routes/Reports';
import { ProfileReports } from '../../../components/ProfileReports';
import { ClinicalComposerButton } from '../../../components/ClinicalComposer';
import {
  Alert, Button, Card, ConfirmDialog, DetailHeader, Dialog, EmptyState, ErrorState, Facts, FitnessDecision as DecisionBanner, IconAction, InfoTip, Input, RowActions, Select,
  StatusBadge, TextField, TextSkeleton, Textarea, cn, fitnessTone, useIdempotencyKey, type Tone,
} from '@dru/ui';
import { api } from '../../../lib/api';
import { useQuery } from '../../../lib/useQuery';
import { errorMessage, useAuth } from '../../../lib/auth';
import { holds } from '../../../layouts/ConsoleShell';
import { CrossCheckPanel, IdentityPanel, LabVerificationPanel } from '../components/ReviewAssurance';

/**
 * The clinical view and the fitness decision.
 *
 * Everything the physician needs to reason with is on one screen — the worker,
 * what they are exposed to, every released result, and the year-on-year trend
 * — because a decision made by paging between screens is a decision made from
 * memory.
 *
 * What changed is the order, not the content. The screen opens on the signals
 * that decide how carefully to read the rest (critical values, anything out of
 * range, whether consent and identity are in order, whether the visit was
 * closed partial), then the findings that need a decision, then the context.
 * Normal results are one click away rather than interleaved with the
 * abnormal ones. The decision panel stays in view while the evidence scrolls.
 *
 * Only released results appear. An entered-but-unvalidated value is not
 * evidence, and the API does not send it here at all.
 */
/**
 * The decision panel's own scroll, made visible. On a landscape tablet the
 * panel is taller than the window and the sign button sits below its fold
 * with no scrollbar to say so; a "More below" bar at its foot does.
 */
function ScrollPanel({ id, className, children }: { id: string; className?: string; children: ReactNode }) {
  const box = useRef<HTMLDivElement>(null);
  const [more, setMore] = useState(false);
  useEffect(() => {
    const el = box.current;
    if (!el) return;
    const check = () => setMore(getComputedStyle(el).overflowY === 'auto' && el.scrollTop + el.clientHeight < el.scrollHeight - 8);
    check();
    el.addEventListener('scroll', check, { passive: true });
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(check) : null;
    ro?.observe(el);
    if (el.firstElementChild) ro?.observe(el.firstElementChild);
    window.addEventListener('resize', check);
    return () => { el.removeEventListener('scroll', check); ro?.disconnect(); window.removeEventListener('resize', check); };
  }, []);
  return (
    <div ref={box} id={id} className={className}>
      {children}
      {more && (
        <button
          type="button"
          onClick={() => box.current?.scrollTo({ top: box.current.scrollHeight, behavior: 'smooth' })}
          className="sticky bottom-0 mt-2 flex w-full items-center justify-center gap-1.5 rounded-btn border border-primary-200 bg-white/95 px-3 py-2.5 text-small font-semibold text-primary-700 shadow-dropdown backdrop-blur"
        >
          <ArrowDown aria-hidden className="h-4 w-4" /> More below — save and sign
        </button>
      )}
    </div>
  );
}

export function Review() {
  const { permissions } = useAuth();
  // The same permission the API checks. Without it nothing is fetched, so no
  // clinical data can flash on screen, and the refusal reads as one.
  if (!holds(permissions, 'review.clinical-read')) return <NotForThisAccount />;
  return <ReviewScreen />;
}

/** What a signed-in account without the physician's permissions sees here. */
export function NotForThisAccount() {
  return (
    <div className="space-y-4">
      <h1 className="text-h2 text-neutral-900">Physician reviews</h1>
      <EmptyState
      icon={<Lock className="h-5 w-5" />}
      title="Physician reviews are not available to your account"
      hint="Clinical results and fitness decisions open only for the physician an examination is assigned to."
      action={<Link to="/" className="text-small font-semibold text-primary-600 hover:underline">Go to your home page</Link>}
      />
    </div>
  );
}

function ReviewScreen() {
  const { id = '' } = useParams();
  const nav = useNavigate();
  const { permissions } = useAuth();
  const canCertify = holds(permissions, 'physician.certify');

  const view = useQuery<ClinicalView>(
    () => api.get(`/physician/examinations/${id}/clinical-view`), [id]);

  if (view.loading) return <TextSkeleton lines={8} />;
  if (view.error || !view.data) {
    return (
      <ErrorState
        title="Could not open this examination"
        detail={view.error ?? undefined}
        onRetry={view.reload}
      />
    );
  }

  const v = view.data;
  const signed = !!v.existingReview?.signedAt;
  const critical = v.results.filter((r) => r.flag === 'CRITICAL_LOW' || r.flag === 'CRITICAL_HIGH');
  const abnormal = v.results.filter((r) => r.flag !== 'NORMAL');
  // A free-text finding is neither: nothing interpreted it. It gets its own
  // list, always open, so it is read rather than folded away as "normal".
  const findings = v.results.filter((r) => r.flag === 'NORMAL' && r.freeText);
  const normal = v.results.filter((r) => r.flag === 'NORMAL' && !r.freeText);

  const where = [v.company, v.employee.plant?.name].filter(Boolean).join(' · ');
  // The record in words, drafted from the released results. Offered to the
  // physician, never written for them: it goes into the advice only when
  // they take it.
  const draftSummary = clinicalSummary(
    v.results.map((r) => ({ code: r.testCode, name: r.testName, value: r.value, unit: r.unit, flag: r.flag, category: r.category })),
    {
      fullName: v.employee.fullName, sex: v.employee.sex,
      ageYears: ageInYears(v.employee.dateOfBirth, v.encounter ? new Date(v.encounter.checkedInAt) : new Date()),
      jobRole: v.employee.jobRole?.title ?? null, examinedOn: v.encounter?.checkedInAt ?? null,
    },
  );

  return (
    <div className="space-y-5 pb-16 lg:pb-0">
      <DetailHeader
        back={signed ? (
          <Link to="/physician/history" className="inline-flex min-h-[24px] items-center gap-1 text-caption text-neutral-500 hover:text-neutral-800">
            <ChevronLeft aria-hidden className="h-4 w-4" /> Review history
          </Link>
        ) : (
          <Link to="/physician/queue" className="inline-flex min-h-[24px] items-center gap-1 text-caption text-neutral-500 hover:text-neutral-800">
            <ChevronLeft aria-hidden className="h-4 w-4" /> Review queue
          </Link>
        )}
        eyebrow={signed
          ? `Signed review · ${EXAM_TYPE_LABEL[v.order.examinationType] ?? v.order.examinationType.replace(/_/g, ' ').toLowerCase()}`
          : EXAM_TYPE_LABEL[v.order.examinationType] ?? v.order.examinationType.replace(/_/g, ' ').toLowerCase()}
        title={v.employee.fullName}
        subtitle={
          <span>
            <span className="font-mono">{v.employee.employeeCode}</span>
            {where ? ` · ${where}` : ''}
            {v.employee.jobRole ? ` · ${v.employee.jobRole.title}` : ''}
            {' · '}{[v.employee.sex?.toLowerCase(), age(v.employee.dateOfBirth)].filter(Boolean).join(', ')}
          </span>
        }
        badges={signed && v.existingReview?.decision
          ? (
            <>
              <StatusBadge tone={fitnessTone(v.existingReview.decision)}>Signed · {FITNESS_LABEL[v.existingReview.decision]}</StatusBadge>
              <StatusBadge tone="neutral"><Lock aria-hidden className="h-3 w-3" /> Read only</StatusBadge>
            </>
          )
          : <StatusBadge tone="pending">Awaiting your decision</StatusBadge>}
      />

      {v.correctionOf && <CorrectionOfBanner view={v} signed={signed} />}
      {v.correctedBy && (
        <Alert tone="info" title="This review was corrected">
          What you signed is kept below exactly as it was signed. The corrected decision was signed on a linked re-review.{' '}
          <Link to={`/physician/review/${v.correctedBy}`} className="font-semibold text-primary-600 hover:underline">
            Open the corrected review
          </Link>
        </Alert>
      )}

      <Signals view={v} critical={critical.length} abnormal={abnormal.length} />

      <div className="grid items-start gap-5 lg:grid-cols-[minmax(0,1fr)_20rem] xl:grid-cols-[minmax(0,1fr)_24rem]">
        {/* The evidence in four groups, in the order a decision is made:
            what is abnormal, who the worker is and what they are exposed to,
            what the laboratory signed, and the rest of the visit. */}
        <div className="min-w-0 space-y-6">
          <Group title="Needs your attention">
            <FindingsPanel abnormal={abnormal} total={v.results.length} trends={v.trends} />
            {findings.length > 0 && (
              <SectionCard title="Written findings" meta={`${findings.length} to read — recorded as text, not interpreted`}>
                <ul className="divide-y divide-neutral-200">
                  {findings.map((r) => <ResultRow key={r.id} r={r} trend={[]} written />)}
                </ul>
              </SectionCard>
            )}
            {v.results.length > 0 && (
              <LabVerificationPanel orderId={id} results={v.results} canAsk={!signed && holds(permissions, 'physician.review')} onChanged={view.reload} />
            )}
          </Group>

          <Group title="Employee and occupational context">
            <div className="grid items-start gap-4 xl:grid-cols-2">
              <Profile view={v} />
              <PreviousCertificate cert={v.previousCertificate ?? null} />
            </div>
            <IdentityPanel base={`/physician/examinations/${id}`} canRecord={!signed && holds(permissions, 'physician.review')} />
          </Group>

          <Group title="Laboratory evidence" hint="Released and signed only — an unvalidated value is never shown here.">
            <LabReportsPanel orderId={id} />
            <ProfileReports audience={{ kind: 'physician', orderId: id, canReview: canCertify }} />
          </Group>

          <Group title="The visit and other results">
            <Visit view={v} />
            <NormalResults results={normal} trends={v.trends} defaultOpen={abnormal.length === 0 && findings.length === 0} />
            <WorkedOut view={v} />
            {draftSummary.paragraphs.length > 0 && (
              <SectionCard collapsible title="The examination in words" meta="drafted from the record — take it into your advice or leave it">
                <div className="space-y-2 px-4 py-3 text-small text-neutral-800">
                  {draftSummary.paragraphs.map((p, i) => <p key={i}>{p}</p>)}
                </div>
              </SectionCard>
            )}
            {signed && <HealthReportsPanel orderId={id} />}
            {canCertify && <ClinicalCompanyConfiguration orderId={id} />}
          </Group>
        </div>

        {/* In view for the whole review. It scrolls on its own when it is
            taller than the window, so the sign button is never out of reach
            behind the evidence. */}
        {/* Signed, the record is what somebody opened the page to read, so on a
            phone it comes before the evidence rather than after it. */}
        <ScrollPanel id="decision" className={cn('scroll-mt-20 lg:sticky lg:top-20 lg:max-h-[calc(100vh-6rem)] lg:overflow-y-auto', signed && 'order-first lg:order-none')}>
          {signed ? (
            <SignedPanel view={v} onChanged={view.reload} />
          ) : !canCertify ? (
            <Card className="p-4">
              <h2 className="text-h4 text-neutral-900">Fitness decision</h2>
              <p className="mt-1 text-small text-neutral-500">Awaiting the assigned physician. Your account can read this examination but not sign it.</p>
            </Card>
          ) : (
            <DecisionPanel
              draftSummary={draftSummary.text || null}
              orderId={id}
              worker={`${v.employee.fullName} · ${v.employee.employeeCode}`}
              examination={EXAM_TYPE_LABEL[v.order.examinationType] ?? v.order.examinationType.replace(/_/g, ' ').toLowerCase()}
              resultCount={v.results.length}
              draft={v.existingReview}
              periodicityMonths={v.order.periodicityMonths ?? null}
              validityMonths={v.order.certificateValidityMonths ?? null}
              isPartial={v.encounter?.completionType === 'PARTIAL'}
              criticalCount={critical.length}
              blocking={[
                ...(v.pendingCorrections ?? []).map((t) => `A correction to ${t} is awaiting validation and release`),
                ...(v.openLabReportAmendment ? [`Laboratory report ${v.openLabReportAmendment.reportNumber} is being amended (revision ${v.openLabReportAmendment.revision} is not final yet)`] : []),
                ...(v.staleLabReport ? [`A result was released after laboratory report ${v.staleLabReport.reportNumber} was signed; the laboratory amends the report`] : []),
                ...((v as { openLabVerifications?: string[] }).openLabVerifications ?? []).map((t) => `The laboratory has not yet answered your verification request on ${t}`),
              ]}
              onDone={() => nav('/physician/queue')}
            />
          )}
        </ScrollPanel>
      </div>

      {/* Below `lg` the panel follows the evidence; this keeps the way to it
          one tap away. */}
      {!signed && canCertify && (
        <a
          href="#decision"
          className="fixed inset-x-4 bottom-4 z-20 flex items-center justify-center gap-2 rounded-btn bg-primary-600 px-4 py-3 text-body font-semibold text-white shadow-dropdown lg:hidden"
        >
          <ArrowDown aria-hidden className="h-4 w-4" /> Go to the fitness decision
        </a>
      )}
    </div>
  );
}

/**
 * The strip read before anything else: how much of this record is unusual,
 * and whether the paperwork the decision rests on is in order.
 */
function Signals({ view: v, critical, abnormal }: { view: ClinicalView; critical: number; abnormal: number }) {
  const prev = v.previousCertificate;
  const enc = v.encounter;
  const identityOk = !!enc?.identityMethod && enc.identityMethod !== 'PHONE_SEARCH';
  const openStations = (enc?.stations ?? []).filter((x) => x.status !== 'COMPLETED' && x.status !== 'SKIPPED').length;
  const items: Array<{ key: string; icon: ReactNode; label: string; value: string; tone: Tone }> = [
    {
      key: 'critical', icon: <Siren className="h-4 w-4" />, label: 'Critical values',
      value: String(critical), tone: critical ? 'danger' : 'neutral',
    },
    {
      key: 'abnormal', icon: <AlertTriangle className="h-4 w-4" />, label: 'Outside range',
      value: `${abnormal} of ${v.results.length}`, tone: abnormal ? 'warning' : 'neutral',
    },
    {
      key: 'previous', icon: <History className="h-4 w-4" />, label: 'Last decision',
      value: prev ? FITNESS_LABEL[prev.decision] : 'First examination',
      tone: prev ? fitnessTone(prev.decision) : 'neutral',
    },
    {
      key: 'consent', icon: <FileCheck2 className="h-4 w-4" />, label: 'Consent',
      value: v.consent ? 'Recorded' : 'Not recorded', tone: v.consent ? 'success' : 'danger',
    },
    {
      key: 'identity', icon: <IdCard className="h-4 w-4" />, label: 'Identity',
      value: !enc?.identityMethod ? 'Not recorded' : identityOk ? 'Verified at check-in' : 'Not verified',
      tone: identityOk ? 'success' : 'warning',
    },
    {
      // Closed is not the same as every station done: say which.
      key: 'visit', icon: <ClipboardCheck className="h-4 w-4" />, label: 'Visit',
      value: !enc ? 'No visit' : enc.completionType === 'PARTIAL' ? 'Closed partial'
        : !enc.completedAt ? 'Not closed'
          : openStations ? `Closed · ${openStations} station${openStations === 1 ? '' : 's'} open` : 'Complete',
      tone: enc?.completionType === 'PARTIAL' || !enc || openStations ? 'warning' : 'neutral',
    },
  ];
  const TEXT: Partial<Record<Tone, string>> = {
    danger: 'text-danger-600', warning: 'text-warning-600', success: 'text-success-600',
    fit: 'text-fit-600', restricted: 'text-restricted-600', tempUnfit: 'text-tempUnfit-600', unfit: 'text-unfit-600',
  };
  const EDGE: Partial<Record<Tone, string>> = {
    danger: 'border-danger-600/40 bg-danger-100', warning: 'border-warning-600/30',
  };
  return (
    <ul aria-label="At a glance" className="grid grid-cols-2 gap-2 sm:grid-cols-3 xl:grid-cols-6">
      {items.map((s) => (
        <li
          key={s.key}
          className={cn('rounded-card border border-neutral-200 bg-white px-3 py-2.5', EDGE[s.tone])}
        >
          <div className={cn('flex items-center gap-1.5 text-caption', TEXT[s.tone] ?? 'text-neutral-500')}>
            <span aria-hidden>{s.icon}</span>{s.label}
          </div>
          <div className={cn('mt-0.5 truncate text-body font-semibold', TEXT[s.tone] ?? 'text-neutral-900')} title={s.value}>
            {s.value}
          </div>
        </li>
      ))}
    </ul>
  );
}

/**
 * What the released height, weight and blood pressure work out to — the BMI
 * band, resting energy, mean arterial pressure. Arithmetic on the results
 * above, not results: nothing here is flagged or counted, and the decision
 * does not rest on it. Shown so the physician reads a BMI of 27.4 with its
 * band and its inputs rather than looking them up.
 */
function WorkedOut({ view }: { view: ClinicalView }) {
  const numericByCode = new Map<string, number>();
  for (const r of view.results) {
    if (typeof r.value === 'number' && Number.isFinite(r.value)) numericByCode.set(r.testCode, r.value);
  }
  const derived = derivedFromCodes(numericByCode, {
    sex: view.employee.sex,
    ageYears: ageInYears(view.employee.dateOfBirth, view.encounter ? new Date(view.encounter.checkedInAt) : new Date()),
  });
  if (!derived.any) return null;
  return (
    <SectionCard collapsible title="Worked out from the readings" meta="not results — nothing here is flagged or counted">
      <DerivedVitalsPanel derived={derived} compact />
    </SectionCard>
  );
}

/** A labelled group of evidence cards: one heading the eye can scan for. */
function Group({ title, hint, children }: { title: string; hint?: string; children: ReactNode }) {
  return (
    <section aria-label={title} className="space-y-3">
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 border-b border-neutral-200 pb-1.5">
        <h2 className="text-micro font-semibold uppercase tracking-wider text-neutral-600">{title}</h2>
        {hint && <span className="text-caption text-neutral-500">{hint}</span>}
      </div>
      {children}
    </section>
  );
}

function SectionCard({ title, meta, children, className, collapsible, defaultOpen = false }: {
  title: ReactNode;
  meta?: ReactNode;
  children: ReactNode;
  className?: string;
  /** Folded until asked for: reading aids, not evidence. */
  collapsible?: boolean;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(!collapsible || defaultOpen);
  if (collapsible) {
    return (
      <Card className={className}>
        <button
          type="button"
          aria-expanded={open}
          onClick={() => setOpen((o) => !o)}
          className={cn('flex w-full flex-wrap items-center justify-between gap-2 px-4 py-3 text-left max-md:min-h-12', open && 'border-b border-neutral-200')}
        >
          <h3 className="text-h4 text-neutral-900">{title}</h3>
          <span className="inline-flex items-center gap-1.5 text-caption font-normal text-neutral-500">
            {meta}
            <ChevronDown aria-hidden className={cn('h-4 w-4 shrink-0 transition-transform', open && 'rotate-180')} />
          </span>
        </button>
        {open && children}
      </Card>
    );
  }
  return (
    <Card className={className}>
      <div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-neutral-200 px-4 py-3">
        <h3 className="text-h4 text-neutral-900">{title}</h3>
        {meta && <span className="text-caption font-normal text-neutral-500">{meta}</span>}
      </div>
      {children}
    </Card>
  );
}

/**
 * What needs a decision: critical values first, then everything else out of
 * range. Drawn larger than the normal results on purpose — these are the
 * numbers the certificate turns on.
 */
function FindingsPanel({ abnormal, total, trends }: {
  abnormal: ClinicalResult[];
  total: number;
  trends: ClinicalView['trends'];
}) {
  const rank = (r: ClinicalResult) => (r.flag === 'CRITICAL_LOW' || r.flag === 'CRITICAL_HIGH' ? 0 : 1);
  const sorted = [...abnormal].sort((a, b) => rank(a) - rank(b));
  return (
    <SectionCard
      title="Findings outside range"
      meta={total === 0 ? 'nothing released yet' : `${abnormal.length} of ${total} released results`}
    >
      {total === 0 ? (
        <p className="flex items-center gap-1 px-4 py-4 text-small text-neutral-500">
          Nothing has been released yet.
          <InfoTip label="About unreleased results">
            An unvalidated value is not evidence and is deliberately not shown here.
          </InfoTip>
        </p>
      ) : sorted.length === 0 ? (
        <p className="px-4 py-4 text-small text-neutral-700">
          Every released result is within its reference range. They are listed under Normal results below.
        </p>
      ) : (
        <ul className="divide-y divide-neutral-200">
          {sorted.map((r) => <ResultRow key={r.id} r={r} trend={trends[r.testCode] ?? []} emphasis />)}
        </ul>
      )}
    </SectionCard>
  );
}

function NormalResults({ results, trends, defaultOpen }: {
  results: ClinicalResult[];
  trends: ClinicalView['trends'];
  defaultOpen: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  if (results.length === 0) return null;
  // Station findings and laboratory values read as two lists, because they
  // come from two rooms and the laboratory's also have a signed document.
  const groups = ([
    ['At the stations', results.filter((r) => r.category !== 'PATHOLOGY')],
    ['From the laboratory', results.filter((r) => r.category === 'PATHOLOGY')],
  ] as Array<[string, ClinicalResult[]]>).filter(([, list]) => list.length > 0);
  return (
    <Card>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left"
      >
        <span className="text-h4 text-neutral-900">Normal results</span>
        <span className="inline-flex items-center gap-1.5 text-caption text-neutral-500">
          {results.length} within range
          <ChevronDown aria-hidden className={cn('h-4 w-4 transition-transform', open && 'rotate-180')} />
        </span>
      </button>
      {open && groups.map(([heading, list]) => (
        <div key={heading} className="border-t border-neutral-200">
          <div className="bg-neutral-50 px-4 py-1.5 text-micro uppercase tracking-wider text-neutral-500">
            {heading} · {list.length}
          </div>
          <ul className="divide-y divide-neutral-200">
            {list.map((r) => <ResultRow key={r.id} r={r} trend={trends[r.testCode] ?? []} />)}
          </ul>
        </div>
      ))}
    </Card>
  );
}

/**
 * One result. A row, not a table cell: the name and the range on the left,
 * the value and its flag on the right, and the history beneath — which folds
 * cleanly into a narrow column without a horizontal scroll.
 */
function ResultRow({ r, trend, emphasis, written }: {
  r: ClinicalResult;
  trend: Array<{ at: string; value: number | null; flag: string }>;
  emphasis?: boolean;
  /** A free-text finding: no flag is shown, because none was judged. */
  written?: boolean;
}) {
  const critical = r.flag === 'CRITICAL_LOW' || r.flag === 'CRITICAL_HIGH';
  const range = rangeText(r.referenceRange);
  return (
    // A critical value is marked three ways — the bar, the icon and the
    // flag's words — so it does not rest on colour.
    <li className={cn('px-4 py-3', critical && 'border-l-4 border-danger-600 bg-danger-100/50 pl-3')}>
      <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-1">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-1.5">
            {critical && <Siren aria-hidden className="h-4 w-4 shrink-0 text-danger-600" />}
            <span className={cn('text-neutral-900', emphasis ? 'text-body font-semibold' : 'text-body')}>{r.testName}</span>
            {r.amended && (
              <StatusBadge
                tone="warning"
                className="py-0"
              >
                <span title={r.priorAttempt
                  ? `Amended from attempt ${r.priorAttempt.attemptNo}: ${String(r.priorAttempt.value ?? '—')}${r.priorAttempt.unit ? ` ${r.priorAttempt.unit}` : ''} (${FLAG_LABEL[r.priorAttempt.flag] ?? r.priorAttempt.flag})`
                  : 'Amended result'}
                >
                  Amended
                </span>
              </StatusBadge>
            )}
          </div>
          <div className="text-caption font-normal text-neutral-500">
            <span className="font-mono">{r.testCode}</span>
            {range && <span className="ml-2">{range}</span>}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <span className={cn('tabular-nums text-neutral-900', emphasis ? 'text-body-lg font-semibold' : 'text-body')}>
            {r.testCode === TOOTH_CHART_CODE ? formatToothChart(String(r.value ?? '')) : String(r.value ?? '—')}{r.unit ? <span className="ml-1 text-small font-normal text-neutral-500">{r.unit}</span> : null}
          </span>
          {!written && <StatusBadge tone={resultTone(r.flag)}>{FLAG_LABEL[r.flag]}</StatusBadge>}
        </div>
      </div>
      {(trend.length > 0 || r.priorAttempt || r.validationComment) && (
        <div className="mt-1.5 space-y-0.5 text-caption font-normal text-neutral-600">
          {/* The shift is the finding, not the value. A 25 dB threshold
              matters differently when last year was 10. */}
          {trend.length > 0 && (
            <p className="tabular-nums">
              <span className="text-neutral-500">Previously: </span>
              {trend.slice(0, 3).map((p) => `${new Date(p.at).getFullYear()}: ${p.value ?? '—'}${r.unit ? ` ${r.unit}` : ''}`).join(' · ')}
            </p>
          )}
          {r.priorAttempt && (
            <p>
              <span className="text-neutral-500">Prior attempt {r.priorAttempt.attemptNo}: </span>
              <span className="tabular-nums font-medium text-neutral-700">
                {String(r.priorAttempt.value ?? '—')}{r.priorAttempt.unit ? ` ${r.priorAttempt.unit}` : ''}
              </span>{' '}
              ({FLAG_LABEL[r.priorAttempt.flag] ?? r.priorAttempt.flag})
              {r.priorAttempt.releasedAt ? ` on ${formatDate(r.priorAttempt.releasedAt)}` : ''}
            </p>
          )}
          {r.validationComment && <p><span className="text-neutral-500">Laboratory note: </span>{r.validationComment}</p>}
        </div>
      )}
    </li>
  );
}

function Profile({ view: v }: { view: ClinicalView }) {
  return (
    <SectionCard title="Employee and exposure">
      <div className="space-y-4 px-4 py-4">
        <Facts
          columns={2}
          // Four across only when the evidence column is wide enough; beside
          // the decision panel on a tablet, two.
          className="grid-cols-2"
          facts={[
            { label: 'Age', value: age(v.employee.dateOfBirth) || '—' },
            { label: 'Sex', value: v.employee.sex ? capitalise(v.employee.sex) : '—' },
            { label: 'Blood group', value: v.employee.bloodGroup ?? '—' },
            { label: 'Job role', value: v.employee.jobRole?.title ?? 'None assigned' },
          ]}
        />
        <div>
          {/* The reason this battery was run at all. A decision made without
              knowing what the worker is exposed to is a general health check,
              not an occupational one. */}
          <div className="text-caption text-neutral-500">Occupational exposure</div>
          <div className="mt-1.5 flex flex-wrap gap-1">
            {v.hazards.length
              ? v.hazards.map((h) => <StatusBadge key={h.code} tone="neutral">{h.name}</StatusBadge>)
              : <span className="text-small text-neutral-500">No hazards mapped</span>}
          </div>
        </div>
      </div>
    </SectionCard>
  );
}

function Visit({ view: v }: { view: ClinicalView }) {
  const enc = v.encounter;
  return (
    <SectionCard title="The visit" meta={enc ? `Checked in ${formatDateTime(enc.checkedInAt)}` : undefined}>
      <div className="space-y-3 px-4 py-4 text-small">
        {!enc ? (
          <p className="text-neutral-500">No visit is recorded against this examination.</p>
        ) : (
          <>
            {enc.identityMethod === 'PHONE_SEARCH' ? (
              <Alert tone="warning" title="Identity not verified at check-in">
                The booking was found by phone search; nothing proved who attended.
              </Alert>
            ) : enc.identityMethod ? (
              <p className="text-success-600">
                Identity verified at check-in — {enc.identityMethod === 'QR' ? 'QR pass' : 'employee code'}.
              </p>
            ) : null}
            {enc.completionType === 'PARTIAL' && (
              <Alert tone="warning" title="Visit closed as partial">
                {enc.partialReasonCode
                  ? `Reason: ${enc.partialReasonCode.replace(/_/g, ' ').toLowerCase()}. `
                  : 'Reason not specified. '}
                One or more mandatory station tests were skipped or left incomplete.
              </Alert>
            )}
            {(enc.attachments ?? []).length > 0 && (
              <div>
                <div className="text-caption font-medium uppercase tracking-wide text-neutral-600">Report files from the stations</div>
                <ul className="mt-1 divide-y divide-neutral-100">
                  {(enc.attachments ?? []).map((a) => (
                    <li key={a.id} className="flex flex-wrap items-center justify-between gap-2 py-1.5">
                      <span className="text-neutral-800">{a.label} <span className="text-neutral-500">· {STATION_LABEL[a.station] ?? a.station}</span></span>
                      <RowActions>
                        <IconAction label={`Open ${a.label}`} icon={Eye} onClick={() => {
                          void api.get<{ url: string }>(`/physician/examinations/${v.order.id}/attachments/${a.id}/download`)
                            .then((g) => window.open(g.url, '_blank', 'noopener'));
                        }} />
                      </RowActions>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <p className="text-neutral-700">
              {v.consent
                ? `Consent given ${formatDate(v.consent.acceptedAt)} (${(CONSENT_METHOD_LABEL[v.consent.method] ?? v.consent.method).toLowerCase()}).`
                : 'Consent not recorded.'}
              {' '}
              {enc.completedAt
                ? `Visit closed ${formatTime(enc.completedAt)}.`
                : 'Visit not yet closed.'}
            </p>
            {(() => {
              const done = enc.stations.filter((x) => x.status === 'COMPLETED').length;
              const skipped = enc.stations.filter((x) => x.status === 'SKIPPED').length;
              const open = enc.stations.length - done - skipped;
              return (
                <p className={cn('font-medium', skipped || open ? 'text-warning-600' : 'text-neutral-800')}>
                  {done} of {enc.stations.length} stations completed
                  {skipped ? ` · ${skipped} skipped` : ''}
                  {open ? ` · ${open} not completed` : ''}
                </p>
              );
            })()}
            <div className="flex flex-wrap gap-1">
              {enc.stations.map((s) => (
                <StatusBadge
                  key={s.id}
                  tone={s.status === 'COMPLETED' ? 'covered' : s.status === 'SKIPPED' ? 'warning' : 'neutral'}
                >
                  {STATION_LABEL[s.station] ?? s.station}
                  {s.status === 'SKIPPED' ? ' (skipped)' : ''}
                </StatusBadge>
              ))}
            </div>
          </>
        )}
      </div>
    </SectionCard>
  );
}

/**
 * The laboratory's signed report, as a document.
 *
 * The findings above extract the values; this is the thing the laboratory
 * actually signed, with its number, its verification block and its
 * amendments. FINAL and AMENDED only — the server sends nothing earlier, so a
 * preliminary result cannot be read here as a final one.
 */
function LabReportsPanel({ orderId }: { orderId: string }) {
  const canDownload = holds(useAuth().permissions, 'lab.report-download');
  const reports = useQuery<LabReportSummary[]>(
    () => api.get(`/physician/examinations/${orderId}/lab-reports`), [orderId]);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function open(id: string, mode: 'view' | 'download') {
    setBusy(`${mode}-${id}`); setError(null);
    try { await openReport(`/physician/examinations/${orderId}/lab-reports/${id}/download`, mode); }
    catch (e) { setError(errorMessage(e)); }
    finally { setBusy(null); }
  }

  const rows = reports.data ?? [];
  if (reports.loading && !reports.data) return null;

  return (
    <SectionCard title="Signed laboratory report" meta={rows.length ? `${rows.length} signed` : 'to follow'}>
      {error && <Alert className="mx-4 mt-3">{error}</Alert>}
      {rows.length === 0 ? (
        <p className="flex items-start gap-1 px-4 py-4 text-small text-neutral-700">
          <span>
            You can sign on the released results; the laboratory’s signed report will be attached when it is issued.
          </span>
          <InfoTip label="About the signed laboratory report">
            The released values above are the numbers the report will carry. If the laboratory later amends
            a value, the case comes back to you as a re-review.
          </InfoTip>
        </p>
      ) : (
        <ul className="divide-y divide-neutral-200">
          {rows.map((r) => (
            <li key={r.id} className="flex flex-wrap items-center justify-between gap-2 px-4 py-2.5">
              <div className="min-w-0">
                <div className="flex items-center gap-2 font-mono text-small text-neutral-900">
                  <FileText aria-hidden className="h-4 w-4 text-neutral-400" />
                  {r.reportNumber}
                  {r.revision > 1 && <span className="font-sans text-caption text-neutral-500">rev {r.revision}</span>}
                </div>
                <div className="text-caption font-normal text-neutral-500">
                  {r.laboratory?.name ?? 'Laboratory'} · {r.lineCount} tests · {r.abnormalCount} outside range
                  {r.criticalCount > 0 ? ` · ${r.criticalCount} critical` : ''}
                  {r.finalisedAt ? ` · ${formatDate(r.finalisedAt)}` : ''}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <StatusBadge tone={r.status === 'FINAL' ? 'covered' : 'neutral'}>
                  {LAB_REPORT_STATUS_LABEL[r.status]}
                </StatusBadge>
                {r.hasDocument && canDownload && (
                  <>
                    <RowActions>
                      <IconAction label={`View report ${r.reportNumber}`} icon={Eye} loading={busy === `view-${r.id}`} onClick={() => open(r.id, 'view')} />
                      <IconAction label={`Download report ${r.reportNumber} (PDF)`} icon={Download} loading={busy === `download-${r.id}`} onClick={() => open(r.id, 'download')} />
                    </RowActions>
                  </>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </SectionCard>
  );
}

/**
 * The worker's Health Check Report, issued when this review was signed. The
 * physician sees the same document the worker will read, which is the point:
 * the advice on its fitness page is theirs, in their words.
 */
function HealthReportsPanel({ orderId }: { orderId: string }) {
  const reports = useQuery<HealthReportSummary[]>(
    () => api.get(`/physician/examinations/${orderId}/health-reports`), [orderId]);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function open(id: string, mode: 'view' | 'download') {
    setBusy(`${mode}-${id}`); setError(null);
    try { await openReport(`/physician/examinations/${orderId}/health-reports/${id}/download`, mode); }
    catch (e) { setError(errorMessage(e)); }
    finally { setBusy(null); }
  }

  const rows = reports.data ?? [];
  if (reports.loading && !reports.data) return null;
  return (
    <SectionCard title="Employee’s health check report" meta={rows.length ? `${rows.length} issued` : 'not issued'}>
      {error && <Alert className="mx-4 mt-3">{error}</Alert>}
      {rows.length === 0 ? (
        <p className="px-4 py-4 text-small text-neutral-500">Issued automatically when the decision is signed. If it did not render, HR can re-issue it from the examination.</p>
      ) : (
        <ul className="divide-y divide-neutral-200">
          {rows.map((r) => (
            <li key={r.id} className="flex flex-wrap items-center justify-between gap-2 px-4 py-2.5">
              <div>
                <div className="font-mono text-small text-neutral-900">{r.reportNumber}{r.revision > 1 && <span className="ml-2 font-sans text-caption text-neutral-500">rev {r.revision}</span>}</div>
                <div className="text-caption font-normal text-neutral-500">
                  {r.summary.measured} parameters · {r.summary.withRange < r.summary.measured ? `${r.summary.inRange} of the ${r.summary.withRange} with a reference range in range · ${r.summary.measured - r.summary.withRange} without a range` : `${r.summary.inRange} in range`}{r.summary.critical ? ` · ${r.summary.critical} critical` : ''}
                  {r.issuedAt ? ` · ${formatDate(r.issuedAt)}` : ''}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <StatusBadge tone={r.status === 'FINAL' ? 'covered' : 'neutral'}>{r.status === 'FINAL' ? 'Final' : 'Superseded'}</StatusBadge>
                {r.hasDocument && (
                  <>
                    <RowActions>
                      <IconAction label={`View report ${r.reportNumber}`} icon={Eye} loading={busy === `view-${r.id}`} onClick={() => open(r.id, 'view')} />
                      <IconAction label={`Download report ${r.reportNumber} (PDF)`} icon={Download} loading={busy === `download-${r.id}`} onClick={() => open(r.id, 'download')} />
                    </RowActions>
                  </>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </SectionCard>
  );
}

/**
 * A laboratory amendment finalised after this decision was signed.
 *
 * The decision and the certificate are untouched: nothing decides medicine
 * automatically. The physician answers explicitly — the decision stands on
 * the amended evidence (with the reason, kept on the record), or the
 * certificate is revoked, which closes the re-review too.
 */
function Reassessments({ view: v, certificate, onChanged }: {
  view: ClinicalView;
  certificate: { id: string; status: string; certificateNumber: string } | null;
  onChanged: () => void;
}) {
  const list = v.reassessments ?? [];
  const { permissions } = useAuth();
  const canResolve = holds(permissions, 'physician.review');
  const canRevoke = holds(permissions, 'certificate.revoke');
  const [resolving, setResolving] = useState<string | null>(null);
  if (!list.length) return null;
  const open = list.filter((r) => !r.resolvedAt);
  const done = list.filter((r) => r.resolvedAt);
  return (
    <div className="mt-3 space-y-2">
      {open.map((r) => (
        <Alert key={r.id} tone="danger" title={`Re-review: report ${r.labReport.reportNumber} amended after you signed`}>
          <span className="text-neutral-800">
            Revision {r.labReport.revision} was finalised {r.labReport.finalisedAt ? formatDateTime(r.labReport.finalisedAt) : ''}
            {v.labReportBasis ? `; your decision rested on revision ${v.labReportBasis.revision}` : ''}.
            {r.labReport.amendmentReason ? ` Reason given: “${r.labReport.amendmentReason}”.` : ''}
            {' '}Review the amended results above, then say whether the decision stands. If it does not, request a
            correction below and re-review it.
          </span>
          <div className="mt-2 flex flex-wrap gap-2">
            {canResolve && <Button size="sm" variant="secondary" onClick={() => setResolving(r.id)}>Decision stands…</Button>}
            {canRevoke && certificate?.status === 'ACTIVE' && (
              <span className="self-center text-caption font-normal text-neutral-600">or revoke the certificate below</span>
            )}
          </div>
        </Alert>
      ))}
      {done.length > 0 && (
        <ul className="space-y-1 text-caption font-normal text-neutral-600">
          {done.map((r) => (
            <li key={r.id}>
              Re-review of revision {r.labReport.revision}: {r.outcome === 'DECISION_STANDS' ? 'decision stands'
                : r.outcome === 'RE_REVIEWED' ? 'closed by a signed correction re-review' : 'certificate revoked'}
              {r.resolvedAt ? ` · ${formatDateTime(r.resolvedAt)}` : ''}{r.note ? ` — “${r.note}”` : ''}
            </li>
          ))}
        </ul>
      )}
      {resolving && (
        <ConfirmDialog
          title="The decision stands"
          what="You have reviewed the amended laboratory results and the signed fitness decision remains valid. The decision and the certificate are not changed."
          requireReason
          reasonLabel="Why the decision stands"
          reasonHint="At least ten characters. Kept on the record with the re-review."
          confirmLabel="Record: decision stands"
          onConfirm={async (note) => {
            if (note.length < 10) throw new Error('Say why in at least ten characters.');
            await api.post(`/physician/examinations/${v.order.id}/reassessments/${resolving}/resolve`, { note }, crypto.randomUUID());
            setResolving(null);
            onChanged();
          }}
          onCancel={() => setResolving(null)}
        />
      )}
    </div>
  );
}

/** What was decided last time, and the limits that were placed. */
function PreviousCertificate({ cert }: { cert: ClinicalView['previousCertificate'] | null }) {
  return (
    <SectionCard title="Last certificate" meta={cert ? <span className="font-mono">{cert.certificateNumber}</span> : undefined}>
      <div className="px-4 py-4 text-small">
        {!cert ? (
          <p className="text-neutral-500">First examination on record — no earlier decision to compare with.</p>
        ) : (
          <div className="space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <StatusBadge tone={fitnessTone(cert.decision)}>{FITNESS_LABEL[cert.decision]}</StatusBadge>
              <StatusBadge tone={certificateTone(cert.status)}>{capitalise(cert.status)}</StatusBadge>
              <span className="text-caption font-normal text-neutral-500">
                issued {formatDate(cert.issuedAt)} · valid to {formatDate(cert.validUntil)}
              </span>
            </div>
            {cert.restrictions.length > 0 && (
              <ul className="flex flex-wrap gap-1">
                {cert.restrictions.map((r) => <li key={r.code}><StatusBadge tone="restricted">{r.label}</StatusBadge></li>)}
              </ul>
            )}
            {cert.clinicalAdvice && (
              <p className="text-neutral-700"><span className="text-neutral-500">Your advice then (private): </span>{cert.clinicalAdvice}</p>
            )}
          </div>
        )}
      </div>
    </SectionCard>
  );
}

const DECISIONS: FitnessDecision[] = [
  'FIT', 'FIT_WITH_RESTRICTION', 'TEMPORARILY_UNFIT', 'UNFIT', 'FURTHER_EVALUATION',
];

const DECISION_NOTE: Record<FitnessDecision, string> = {
  FURTHER_EVALUATION: 'The assessment is incomplete. Further clinical evaluation is required before clearance; no certificate of fitness is issued.',
  FIT: 'No restriction. A certificate is issued.',
  FIT_WITH_RESTRICTION:
    'The employee may work, with coded conditions the employer must apply. A certificate is issued carrying them.',
  TEMPORARILY_UNFIT:
    'Not fit today, expected to become fit. The retest date is what makes it temporary — no certificate is issued.',
  UNFIT: 'Not fit for this job. No certificate is issued; HR acts on the decision.',
};

/** Whether signing this decision issues a certificate — FIT and FIT_WITH_RESTRICTION do. */
const ISSUES_CERTIFICATE: Record<FitnessDecision, boolean> = {
  FIT: true, FIT_WITH_RESTRICTION: true, TEMPORARILY_UNFIT: false, UNFIT: false, FURTHER_EVALUATION: false,
};

/**
 * The signed review, read only — exactly what was signed, by whom and when.
 *
 * Nothing here edits it, and the API refuses anything that would. What stays
 * open to the signer: answering a laboratory re-review, withdrawing the
 * certificate, and asking for a correction — which, once approved, is signed
 * afresh on a linked re-review while this record stays as it is.
 */
function SignedPanel({ view: v, onChanged }: { view: ClinicalView; onChanged: () => void }) {
  const r = v.existingReview!;
  const cert = r.certificate ?? null;
  const applied = r.restrictions ?? [];
  const visibleFindings = (r.findings ?? []).filter((f) => f.employeeVisible);
  const privateFindings = (r.findings ?? []).filter((f) => !f.employeeVisible);
  const canRevoke = holds(useAuth().permissions, 'certificate.revoke');
  const registration = r.physician?.registrationNo
    ?? (r.signatureRef?.startsWith('physician:') ? r.signatureRef.slice('physician:'.length) : null);
  const [revoking, setRevoking] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function revoke() {
    if (!cert) return;
    setBusy(true); setError(null);
    try {
      await api.post(`/physician/certificates/${cert.id}/revoke`, { reason: reason.trim() });
      setRevoking(false); onChanged();
    } catch (e) { setError(errorMessage(e)); } finally { setBusy(false); }
  }

  return (
    <Card className="p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-1">
          <h2 className="flex items-center gap-1.5 text-h4 text-neutral-900">
            <Lock aria-hidden className="h-4 w-4 text-neutral-500" /> Signed review
          </h2>
          <InfoTip label="About a signed review">
            A signed review is never edited. If something in it is wrong, request a correction: once it is approved you
            re-review the same evidence and sign again, and this record stays exactly as it is.
          </InfoTip>
        </div>
        <StatusBadge tone="neutral">Read only</StatusBadge>
      </div>
      <p className="mt-1 text-caption font-normal text-neutral-500">
        Signed {formatDateTime(r.signedAt!)}
        {r.physician ? <> by <span className="font-medium text-neutral-700">{r.physician.fullName}</span></> : null}
        {registration ? <> against registration <span className="font-mono">{registration}</span></> : null}.
      </p>
      {v.labReportBasis && (
        <p className="mt-1 text-caption font-normal text-neutral-500">
          Rested on laboratory report <span className="font-mono">{v.labReportBasis.reportNumber}</span>, revision {v.labReportBasis.revision}.
        </p>
      )}
      <Reassessments view={v} certificate={cert} onChanged={onChanged} />
      {v.labReportAmendedAfterSigning && !(v.reassessments ?? []).length && (
        <Alert tone="warning" title="Laboratory report amended after signing" className="mt-3">
          A newer revision of the laboratory report was finalised after this decision was signed. Review the amended
          results; if the decision is affected, request a correction or revoke the certificate below.
        </Alert>
      )}

      {/* The same split as the unsigned form: what the company reads, and
          what stays in the clinical record. */}
      <section aria-labelledby="signed-employer-visible" className="mt-3 rounded-input border border-neutral-200">
        <h3 id="signed-employer-visible" className="flex items-center gap-1.5 border-b border-neutral-200 bg-neutral-50 px-3 py-2 text-caption text-neutral-700">
          <Eye aria-hidden className="h-3.5 w-3.5" /> Visible to the employer
        </h3>
        <div className="space-y-2 px-3 py-3 text-small">
          {r.decision && (
            <DecisionBanner
              decision={r.decision}
              detail={r.decision === 'TEMPORARILY_UNFIT' && r.retestDueDate ? `Retest due ${formatDate(r.retestDueDate)}` : undefined}
            />
          )}
          {applied.length > 0 && (
            <div>
              <p className="text-caption text-neutral-500">Restrictions</p>
              <ul className="mt-1 flex flex-wrap gap-1">
                {applied.map((a) => (
                  <li key={a.restrictionId}>
                    <StatusBadge tone="restricted">{a.restriction?.label ?? 'Restriction'}</StatusBadge>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {r.followUpMonths ? (
            <p className="text-neutral-700"><span className="text-neutral-500">Next review in </span>{r.followUpMonths} months</p>
          ) : null}
          {visibleFindings.length > 0 && (
            <div>
              <p className="text-caption text-neutral-500">Findings shared with the employee</p>
              <ul className="mt-1 space-y-0.5 text-neutral-800">
                {visibleFindings.map((f) => <li key={f.id}><span className="text-neutral-500">{f.category}: </span>{f.finding}</li>)}
              </ul>
            </div>
          )}
          {cert ? (
            <div className="space-y-1 border-t border-neutral-200 pt-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="min-w-0">
                  <span className="block text-caption font-normal text-neutral-500">Certificate</span>
                  <span className="break-all font-mono text-caption text-neutral-800">{cert.certificateNumber}</span>
                </span>
                <StatusBadge tone={certificateTone(cert.status)}>{capitalise(cert.status)}</StatusBadge>
              </div>
              {cert.issuedAt && (
                <p className="text-caption font-normal text-neutral-500">
                  Issued {formatDate(cert.issuedAt)}{cert.validUntil ? ` · valid to ${formatDate(cert.validUntil)}` : ''}
                </p>
              )}
              {cert.status === 'REVOKED' && cert.revokedReason && (
                <p className="text-caption font-normal text-neutral-600">Revoked{cert.revokedAt ? ` ${formatDate(cert.revokedAt)}` : ''}: {cert.revokedReason}</p>
              )}
              {cert.status === 'SUPERSEDED' && (
                <p className="text-caption font-normal text-neutral-600">Replaced by the certificate issued on the corrected review.</p>
              )}
            </div>
          ) : r.decision && !ISSUES_CERTIFICATE[r.decision] ? (
            <p className="border-t border-neutral-200 pt-2 text-caption font-normal text-neutral-500">No certificate is issued for this decision.</p>
          ) : null}
        </div>
      </section>

      <section aria-labelledby="signed-private-record" className="mt-3 space-y-1.5 rounded-input border border-dashed border-neutral-300 bg-neutral-50 px-3 py-2 text-small">
        <h3 id="signed-private-record" className="inline-flex items-center gap-1.5 text-caption text-neutral-700">
          <Lock aria-hidden className="h-3.5 w-3.5" /> Private clinical record — not shown to the employer
        </h3>
        <p className={cn('whitespace-pre-line', r.clinicalAdvice ? 'text-neutral-800' : 'text-neutral-500')}>
          {r.clinicalAdvice || 'No clinical advice was recorded.'}
        </p>
        {r.remarks && <p className="whitespace-pre-line text-neutral-800"><span className="text-neutral-500">Remarks: </span>{r.remarks}</p>}
        {privateFindings.length > 0 && (
          <ul className="space-y-0.5 text-neutral-800">
            {privateFindings.map((f) => <li key={f.id}><span className="text-neutral-500">{f.category}: </span>{f.finding}</li>)}
          </ul>
        )}
      </section>

      <Corrections view={v} onChanged={onChanged} />

      {canRevoke && cert?.status === 'ACTIVE' && (
        <div className="mt-4 border-t border-neutral-200 pt-3">
          <p className="text-caption font-normal text-neutral-500">
            If a result this decision rested on turns out wrong, withdraw the certificate. This cannot be undone.
          </p>
          <Button size="sm" variant="danger" className="mt-2" onClick={() => setRevoking(true)}>Revoke certificate…</Button>
        </div>
      )}
      {revoking && cert && (
        <Dialog title={`Revoke ${cert.certificateNumber}`} onClose={() => setRevoking(false)}>
          <p className="mt-1 text-body text-neutral-700">
            The certificate stops being valid at once: a gate scan answers REVOKED, the employee's
            app shows them as not currently certified, and HR sees the withdrawal. This is not
            undone — a new examination is how the employee is certified again.
          </p>
          <TextField label="Why" value={reason} onChange={setReason} required autoFocus
            hint="At least ten characters. Printed on the record and the audit trail."
            placeholder="Haemoglobin result amended by the laboratory after release" />
          {error && <Alert className="mt-3">{error}</Alert>}
          <div className="mt-5 flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setRevoking(false)} disabled={busy}>Keep it</Button>
            <Button variant="danger" onClick={revoke} loading={busy} disabled={reason.trim().length < 10}>Revoke certificate</Button>
          </div>
        </Dialog>
      )}
    </Card>
  );
}

/**
 * Corrections of this signed review: what was asked, what was decided, and
 * the way to ask. Asking is not editing — it waits for an authorised
 * reviewer, and nothing about the review changes while it does.
 */
function Corrections({ view: v, onChanged }: { view: ClinicalView; onChanged: () => void }) {
  const canRequest = holds(useAuth().permissions, 'physician.review');
  const list = v.corrections ?? [];
  const open = list.find((c) => c.status === 'PENDING' || c.status === 'APPROVED');
  const corrected = list.find((c) => c.status === 'COMPLETED');
  const [asking, setAsking] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function cancel(id: string) {
    setBusy(id); setError(null);
    try { await api.post(`/physician/examinations/${v.order.id}/corrections/${id}/cancel`, {}); onChanged(); }
    catch (e) { setError(errorMessage(e)); } finally { setBusy(null); }
  }

  return (
    <section aria-labelledby="corrections" className="mt-4 border-t border-neutral-200 pt-3">
      <h3 id="corrections" className="flex items-center gap-1.5 text-caption text-neutral-700">
        <FilePenLine aria-hidden className="h-3.5 w-3.5" /> Corrections
      </h3>
      {list.length === 0 ? (
        <p className="mt-1 text-caption font-normal text-neutral-500">No correction has been requested.</p>
      ) : (
        <ul className="mt-1.5 space-y-2">
          {list.map((c) => (
            <li key={c.id} className="rounded-input border border-neutral-200 px-3 py-2 text-small">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="font-medium text-neutral-900">{CORRECTION_TYPE_LABEL[c.type]}</span>
                <StatusBadge tone={c.status === 'PENDING' || c.status === 'APPROVED' ? 'pending' : c.status === 'REJECTED' ? 'warning' : 'neutral'}>
                  {CORRECTION_STATUS_LABEL[c.status]}
                </StatusBadge>
              </div>
              <p className="mt-0.5 text-neutral-800">{c.reason}</p>
              <p className="mt-0.5 whitespace-pre-line text-caption font-normal text-neutral-600">{c.detail}</p>
              {(() => {
                const f = c as typeof c & { fieldName?: string | null; originalValue?: string | null; proposedValue?: string | null; supportingInfo?: string | null; requesterRole?: string };
                return f.proposedValue || f.requesterRole === 'REVIEWER' ? (
                  <p className="mt-0.5 text-caption font-normal text-neutral-700">
                    {f.requesterRole === 'REVIEWER' ? 'Reopened by a reviewer for cross-checking. ' : ''}
                    {f.fieldName ? <>{f.fieldName}: </> : null}
                    {f.originalValue ? <>“{f.originalValue}” → </> : null}
                    {f.proposedValue ? <strong className="font-semibold">“{f.proposedValue}”</strong> : null}
                    {f.supportingInfo ? <> · {f.supportingInfo}</> : null}
                  </p>
                ) : null;
              })()}
              <p className="mt-1 text-caption font-normal text-neutral-500">
                Requested {formatDateTime(c.requestedAt)}
                {c.reviewedAt ? ` · decided ${formatDateTime(c.reviewedAt)}` : ''}
              </p>
              {c.resolutionNote && <p className="mt-0.5 text-caption font-normal text-neutral-600">“{c.resolutionNote}”</p>}
              {c.status === 'COMPLETED' && (
                <p className="mt-0.5 text-caption font-normal text-neutral-600">
                  {c.decisionChanged ? 'Decision changed on re-review.' : 'Decision unchanged on re-review.'}
                  {c.certificateOutcome ? ` ${CORRECTION_OUTCOME_LABEL[c.certificateOutcome]}.` : ''}
                </p>
              )}
              <div className="mt-1.5 flex flex-wrap gap-2">
                {c.correctionOrderId && (c.status === 'APPROVED' || c.status === 'COMPLETED') && (
                  <Link to={`/physician/review/${c.correctionOrderId}`} className="text-caption font-semibold text-primary-600 hover:underline">
                    {c.status === 'APPROVED' ? 'Open the re-review' : 'Open the corrected review'}
                  </Link>
                )}
                {c.status === 'PENDING' && canRequest && (
                  <Button size="sm" variant="ghost" loading={busy === c.id} onClick={() => cancel(c.id)}>Withdraw request</Button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
      {error && <Alert className="mt-2">{error}</Alert>}
      {canRequest && !open && !corrected && (
        <Button size="sm" variant="secondary" className="mt-2" onClick={() => setAsking(true)}>
          <FilePenLine aria-hidden className="h-3.5 w-3.5" /> Request correction
        </Button>
      )}
      {asking && (
        <RequestCorrectionDialog
          orderId={v.order.id}
          onClose={() => setAsking(false)}
          onDone={() => { setAsking(false); onChanged(); }}
        />
      )}
    </section>
  );
}

const CORRECTION_TYPES: CorrectionType[] = ['DECISION', 'RESTRICTION', 'CLINICAL_ADVICE', 'FOLLOW_UP', 'FINDING', 'OTHER'];

function RequestCorrectionDialog({ orderId, onClose, onDone }: { orderId: string; onClose: () => void; onDone: () => void }) {
  const [type, setType] = useState<CorrectionType | ''>('');
  const [reason, setReason] = useState('');
  const [detail, setDetail] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // One key per dialog, so a double-tapped Submit files one request.
  const idem = useIdempotencyKey();
  // Field-level detail: what it should say, and on what evidence. What it
  // said is read from the signed record by the server.
  const [proposedValue, setProposedValue] = useState('');
  const [supportingInfo, setSupportingInfo] = useState('');
  const ready = type !== '' && reason.trim().length >= 3 && detail.trim().length >= 10 && proposedValue.trim().length > 0;

  async function submit() {
    setBusy(true); setError(null);
    try {
      await api.post(`/physician/examinations/${orderId}/corrections`, {
        type, reason: reason.trim(), detail: detail.trim(),
        ...(proposedValue.trim() ? { proposedValue: proposedValue.trim() } : {}),
        ...(supportingInfo.trim() ? { supportingInfo: supportingInfo.trim() } : {}),
      }, idem.key());
      onDone();
    } catch (e) { setError(errorMessage(e)); setBusy(false); }
  }

  return (
    <Dialog title="Request correction" onClose={() => { if (!busy) onClose(); }}>
      <p className="mt-1 text-small text-neutral-700">
        This does not edit your signed review. It asks for a re-review: once an authorised reviewer approves it, the
        examination returns to your queue, you read the same evidence and sign again, and the review you signed stays
        on the record exactly as it is.
      </p>
      <fieldset className="mt-4">
        <legend className="text-caption text-neutral-700">What needs correction?</legend>
        <div className="mt-1.5 grid gap-1.5 sm:grid-cols-2">
          {CORRECTION_TYPES.map((t) => (
            <label
              key={t}
              className={cn(
                'flex min-h-[44px] cursor-pointer items-center gap-2.5 rounded-input border px-3 py-2 text-small',
                type === t ? 'border-primary-600 bg-primary-50 ring-1 ring-primary-600' : 'border-neutral-200 hover:bg-neutral-50',
              )}
            >
              <input type="radio" name="correction-type" checked={type === t} onChange={() => setType(t)} className="h-4 w-4 accent-primary-600" />
              {CORRECTION_TYPE_LABEL[t]}
            </label>
          ))}
        </div>
      </fieldset>
      <TextField label="Reason" value={reason} onChange={setReason} required
        placeholder="Night-shift restriction omitted"
        hint="A few words. The reviewer and the audit trail read this." />
      <label className="mt-3 block">
        <span className="text-caption text-neutral-700">Detailed explanation <span className="text-danger-600">*</span></span>
        <Textarea value={detail} onChange={(e) => setDetail(e.target.value)} rows={4} className="mt-1"
          placeholder="What is wrong in the signed review, and what it should have said." />
      </label>
      <TextField label="Proposed value or decision" value={proposedValue} onChange={setProposedValue} required
        placeholder="Fit with restriction: no night shifts"
        hint="What the corrected field should say. The current value is taken from your signed review." />
      <label className="mt-3 block">
        <span className="text-caption text-neutral-700">Supporting information</span>
        <Textarea value={supportingInfo} onChange={(e) => setSupportingInfo(e.target.value)} rows={2} className="mt-1"
          placeholder="The finding, report or conversation this rests on (optional)." />
      </label>
      {error && <Alert className="mt-3">{error}</Alert>}
      <div className="mt-5 flex flex-wrap justify-end gap-2">
        <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
        <Button onClick={submit} loading={busy} disabled={!ready}>Submit request</Button>
      </div>
    </Dialog>
  );
}

/** On a correction re-review: what is being corrected, and that it is kept. */
function CorrectionOfBanner({ view: v, signed }: { view: ClinicalView; signed: boolean }) {
  const c = v.correctionOf!;
  const original = c.review;
  return (
    <Alert tone="info" title={signed ? 'Correction re-review — signed' : 'Correction re-review'}>
      <span className="text-neutral-800">
        {signed ? 'This review corrected' : 'You are re-reviewing'} your review signed{' '}
        {original?.signedAt ? formatDateTime(original.signedAt) : ''}
        {original?.decision ? <> ({FITNESS_LABEL[original.decision]}{original.certificate ? `, certificate ${original.certificate.certificateNumber}` : ''})</> : null}.
        {' '}Correction asked: <strong className="font-semibold">{CORRECTION_TYPE_LABEL[c.request.type]}</strong> — “{c.request.reason}”.
        {c.request.resolutionNote ? ` Approved with the note “${c.request.resolutionNote}”.` : ''}
        {!signed && ' The evidence below is the same examination’s; read it again and sign afresh. The original review is kept exactly as it was.'}
      </span>
      <Link to={`/physician/review/${c.orderId}`} className="mt-1 block text-small font-semibold text-primary-600 hover:underline">
        Open the original signed review
      </Link>
    </Alert>
  );
}

const FOLLOW_UP_MONTHS = [3, 6, 12, 24];
/** The validity a certificate gets when the order names none (certificate.service DEFAULT_VALIDITY_MONTHS). */
const DEFAULT_VALIDITY_MONTHS = 12;

function DecisionPanel({ orderId, worker, examination, resultCount, draft, draftSummary, periodicityMonths, validityMonths, isPartial, criticalCount, blocking, onDone }: {
  orderId: string;
  /** The record in words, offered for the private advice; null when nothing was recorded. */
  draftSummary?: string | null;
  worker: string;
  /** What is being certified, restated in the signing dialog. */
  examination: string;
  resultCount: number;
  draft: ClinicalView['existingReview'];
  periodicityMonths: number | null;
  /** The certificate's own validity, from the order. Twelve months when the order says nothing. */
  validityMonths: number | null;
  isPartial?: boolean;
  criticalCount: number;
  /** Evidence still changing. The server refuses to certify on the same checks. */
  blocking: string[];
  onDone: () => void;
}) {
  const codes = useQuery<CursorPage<RestrictionCodeRow>>(
    () => api.get('/physician/restriction-codes'), []);
  const canRetest = holds(useAuth().permissions, 'physician.review');

  const [decision, setDecision] = useState<FitnessDecision | ''>('');
  const [selected, setSelected] = useState<string[]>([]);
  const [retestDueDate, setRetestDueDate] = useState('');
  // Restored from the draft, so a physician called away mid-review finds
  // their advice where they left it rather than typing it twice.
  const [clinicalAdvice, setClinicalAdvice] = useState(draft?.clinicalAdvice ?? '');
  // The protocol's own periodicity is the default, not twelve regardless.
  // The protocol says how often this examination repeats, and a certificate is
  // never valid past that. Offering "24 months" on a three-monthly protocol
  // let the certificate say one thing and the review another, so the choices
  // stop at the protocol's own interval.
  //
  // The certificate's validity caps it as well, and is the default: a
  // 24-month review beside a 12-month certificate printed two periods that
  // disagree (flow audit 2026-10-20, 13.3). The certificate is never valid
  // past the review, so the review is offered up to the validity and starts
  // at it.
  const ceiling = Math.min(periodicityMonths || Infinity, validityMonths || DEFAULT_VALIDITY_MONTHS);
  const followUpChoices = FOLLOW_UP_MONTHS.filter((m) => m <= ceiling);
  if (Number.isFinite(ceiling) && !followUpChoices.includes(ceiling)) followUpChoices.push(ceiling);
  if (!followUpChoices.length) followUpChoices.push(FOLLOW_UP_MONTHS[0]!);
  const [followUpMonths, setFollowUpMonths] = useState(String(followUpChoices[followUpChoices.length - 1]));
  const [codeQuery, setCodeQuery] = useState('');
  const [retesting, setRetesting] = useState(false);
  const [retestReason, setRetestReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  // Requirement 3: the pre-sign cross-check, answered for this decision.
  const [crossChecked, setCrossChecked] = useState(false);
  const [draftState, setDraftState] = useState<'idle' | 'saving' | 'saved' | 'failed'>('idle');

  // Autosaved a moment after typing stops, and on demand. The draft holds the
  // free text only — the decision itself is never stored until it is signed.
  const lastSaved = useRef(draft?.clinicalAdvice ?? '');
  // Signing must never race an autosave. The server refuses a draft that
  // lands after the signature (the write is conditional on the row being
  // unsigned); here, opening the confirmation cancels the pending save and
  // signing waits for one already in flight, so that refusal stays rare.
  const pending = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inFlight = useRef<Promise<void> | null>(null);
  function saveDraft(text: string): Promise<void> {
    setDraftState('saving');
    const run = (async () => {
      try {
        await api.post(`/physician/examinations/${orderId}/draft`, { clinicalAdvice: text || undefined });
        lastSaved.current = text;
        setDraftState('saved');
      } catch { setDraftState('failed'); }
    })();
    inFlight.current = run;
    void run.finally(() => { if (inFlight.current === run) inFlight.current = null; });
    return run;
  }
  function cancelPendingSave() {
    if (pending.current) { clearTimeout(pending.current); pending.current = null; }
  }
  useEffect(() => {
    if (confirming || clinicalAdvice === lastSaved.current) return;
    pending.current = setTimeout(() => { pending.current = null; void saveDraft(clinicalAdvice); }, 1500);
    return cancelPendingSave;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clinicalAdvice, orderId, confirming]);

  const needsRestrictions = decision === 'FIT_WITH_RESTRICTION';
  const needsRetest = decision === 'TEMPORARILY_UNFIT';

  // Mirrors the server's shape rules so the physician is told what is missing
  // before signing, not after. The API remains the authority on all of them.
  const missing = [
    blocking.length > 0 && 'Wait for the evidence to settle (see above)',
    decision === '' && 'Choose a decision',
    needsRestrictions && selected.length === 0 && 'Choose at least one restriction',
    needsRetest && retestDueDate === '' && 'Set the retest date',
    decision !== '' && !crossChecked && 'Run and answer the pre-sign cross-check',
  ].filter(Boolean) as string[];
  const ready = missing.length === 0;
  const restrictionRows = (codes.data?.items ?? []).filter((c) => selected.includes(c.code));

  function toggle(code: string) {
    setSelected((s) => (s.includes(code) ? s.filter((c) => c !== code) : [...s, code]));
  }

  async function sign() {
    setBusy(true); setError(null);
    cancelPendingSave();
    // The advice goes with the signature itself, so nothing is lost by not
    // saving it as a draft first; waiting only keeps the two from crossing.
    await inFlight.current?.catch(() => undefined);
    try {
      await api.post(`/physician/examinations/${orderId}/certify`, {
        decision,
        ...(needsRestrictions ? { restrictionCodes: selected } : {}),
        ...(needsRetest ? { retestDueDate: new Date(retestDueDate).toISOString() } : {}),
        ...(clinicalAdvice.trim() ? { clinicalAdvice: clinicalAdvice.trim() } : {}),
        ...(followUpMonths ? { followUpMonths: Number(followUpMonths) } : {}),
      }, crypto.randomUUID());
      onDone();
    } catch (e) {
      setError(errorMessage(e));
      setConfirming(false);
    } finally { setBusy(false); }
  }

  const draftLabel = draftState === 'saving' ? 'Saving…' : draftState === 'saved' ? 'Draft saved'
    : draftState === 'failed' ? 'Draft not saved' : '';

  return (
    <Card>
      <div className="border-b border-neutral-200 px-4 py-3">
        <h2 className="text-h4 text-neutral-900">Fitness decision</h2>
        <p className="mt-0.5 text-caption font-normal text-neutral-500">
          Signed in your name against your registration number. This is a statutory act.
        </p>
      </div>

      <div className="space-y-4 px-4 py-4">
        {isPartial && (
          <Alert tone="warning" title="Incomplete visit">
            This encounter was closed as partial. Statutory certification under the Factories Act requires the
            mandatory prescribed tests; make sure what is missing does not impair your assessment.
          </Alert>
        )}
        {blocking.length > 0 && (
          <Alert tone="danger" title="Not signable yet — the evidence is changing">
            {blocking.join('. ')}. A decision is signed on the standing evidence.
          </Alert>
        )}
        {criticalCount > 0 && (
          <Alert tone="danger" title={`${criticalCount} critical value${criticalCount === 1 ? '' : 's'} released`}>
            Listed first under Findings outside range.
          </Alert>
        )}

        <fieldset>
          <legend className="sr-only">Decision</legend>
          <div className="space-y-1.5">
            {DECISIONS.map((d) => (
              <label
                key={d}
                className={cn(
                  'flex cursor-pointer items-start gap-3 rounded-input border px-3 py-2.5 transition-colors duration-hover',
                  decision === d ? 'border-primary-600 bg-primary-50 ring-1 ring-primary-600' : 'border-neutral-200 hover:bg-neutral-50',
                )}
              >
                <input
                  type="radio"
                  name="decision"
                  checked={decision === d}
                  onChange={() => { setDecision(d); setSelected([]); setConfirming(false); }}
                  className="mt-1 h-4 w-4 accent-primary-600"
                />
                <span className="min-w-0">
                  <StatusBadge tone={fitnessTone(d)}>{FITNESS_LABEL[d]}</StatusBadge>
                  {decision === d && <span className="mt-1 block text-caption font-normal text-neutral-600">{DECISION_NOTE[d]}</span>}
                </span>
              </label>
            ))}
          </div>
        </fieldset>

        {/* What the company will read. Kept visibly apart from the private
            record below: the one mistake this form must make impossible is
            typing a diagnosis where the employer will see it. */}
        <section aria-labelledby="employer-visible" className="rounded-input border border-neutral-200">
          <h3 id="employer-visible" className="flex items-center gap-1.5 border-b border-neutral-200 bg-neutral-50 px-3 py-2 text-caption text-neutral-700">
            <Eye aria-hidden className="h-3.5 w-3.5" /> Visible to the employer
            <InfoTip label="About what the employer sees">
              With the decision and your sign-off, this is all the company sees.
            </InfoTip>
          </h3>
          <div className="space-y-3 px-3 py-3">
            {needsRestrictions && (
              <div>
                <span className="text-caption text-neutral-700">
                  Restrictions <span className="font-normal text-neutral-500">(at least one)</span>
                </span>
                {codes.error && (
                  <p className="mt-1 text-caption text-danger-600">
                    The restriction catalogue could not be loaded: {codes.error}
                  </p>
                )}
                <Input value={codeQuery} onChange={(e) => setCodeQuery(e.target.value)} placeholder="Find a restriction…" aria-label="Find a restriction" className="mt-1.5" />
                <ul className="mt-2 max-h-52 space-y-0.5 overflow-y-auto rounded-input border border-neutral-200 p-1.5">
                  {(codes.data?.items ?? [])
                    .filter((c) => selected.includes(c.code) || !codeQuery || c.label.toLowerCase().includes(codeQuery.toLowerCase()) || c.code.toLowerCase().includes(codeQuery.toLowerCase()))
                    .map((c) => (
                      <li key={c.id}>
                        <label className="flex items-start gap-2 rounded px-1.5 py-1 hover:bg-neutral-50">
                          <input
                            type="checkbox"
                            checked={selected.includes(c.code)}
                            onChange={() => toggle(c.code)}
                            className="mt-0.5 accent-primary-600"
                          />
                          <span className="min-w-0">
                            <span className="block text-small text-neutral-800">{c.label}</span>
                            <span className="block font-mono text-micro font-normal text-neutral-500">{c.code}</span>
                          </span>
                        </label>
                      </li>
                    ))}
                </ul>
              </div>
            )}

            {needsRetest && (
              <div>
                <span className="flex items-center gap-1">
                  <label htmlFor="retest-due" className="text-caption text-neutral-700">Retest due</label>
                  <InfoTip label="About the retest date">
                    This date is the difference between temporarily unfit and unfit. Without it the decision is permanent.
                  </InfoTip>
                </span>
                <Input
                  id="retest-due"
                  type="date"
                  value={retestDueDate}
                  onChange={(e) => setRetestDueDate(e.target.value)}
                  min={new Date().toISOString().slice(0, 10)}
                  className="mt-1"
                />
              </div>
            )}

            <label className="block">
              <span className="text-caption text-neutral-700">Next review in</span>
              <Select value={followUpMonths} onChange={(e) => setFollowUpMonths(e.target.value)} className="mt-1">
                {followUpChoices.map((m) => <option key={m} value={m}>{m} months</option>)}
              </Select>
              {ceiling < 24 && (
                <span className="mt-1 block text-caption font-normal text-neutral-500">
                  {periodicityMonths != null && periodicityMonths <= ceiling
                    ? `This examination repeats every ${periodicityMonths} month${periodicityMonths === 1 ? '' : 's'}, so a later review cannot be set.`
                    : `The certificate is valid for ${ceiling} month${ceiling === 1 ? '' : 's'}, so a later review cannot be set. A shorter review shortens the certificate.`}
                </span>
              )}
            </label>
          </div>
        </section>

        <section aria-labelledby="private-record" className="rounded-input border border-dashed border-neutral-300 bg-neutral-50">
          <h3 id="private-record" className="flex items-center justify-between gap-2 px-3 pt-2 text-caption text-neutral-700">
            <span className="inline-flex items-center gap-1.5">
              <Lock aria-hidden className="h-3.5 w-3.5" /> Private clinical record
              <InfoTip label="About the private clinical record">
                Never shown to the employer. Anything the employer must act on belongs in a restriction.
              </InfoTip>
            </span>
            <span aria-live="polite" className={cn('font-normal', draftState === 'failed' ? 'text-danger-600' : 'text-neutral-500')}>{draftLabel}</span>
          </h3>
          <div className="px-3 pb-3 pt-1.5">
            <label className="block">
              <span className="sr-only">Clinical advice — private clinical record</span>
              <Textarea
                value={clinicalAdvice}
                onChange={(e) => setClinicalAdvice(e.target.value)}
                rows={3}
                placeholder="Clinical advice for the record"
              />
            </label>
            {/* Requirement 4: shortcuts, templates, favourites, voice, AI draft.
                The text lands here only when the physician accepts it. */}
            <ClinicalComposerButton
              contexts={['CONSULTATION', 'MEDICAL_HISTORY', 'PRESCRIPTION', 'REFERRAL', 'INVESTIGATION', 'CERTIFICATE']}
              value={clinicalAdvice} onAccept={setClinicalAdvice} className="mt-1"
            />
            {draftSummary && !clinicalAdvice.includes(draftSummary.slice(0, 40)) && (
              <Button
                size="sm" variant="ghost" className="mt-1"
                onClick={() => setClinicalAdvice((t) => (t.trim() ? `${t.trimEnd()}\n\n${draftSummary}` : draftSummary))}
                title="Appends the drafted summary of the record to your advice; edit it as you like"
              >
                Insert the record in words
              </Button>
            )}
          </div>
        </section>

        <CrossCheckPanel
          orderId={orderId}
          decision={decision}
          restrictionCodes={needsRestrictions ? selected : []}
          clinicalAdvice={clinicalAdvice}
          followUpMonths={followUpMonths ? Number(followUpMonths) : undefined}
          onReady={setCrossChecked}
        />

        {error && <Alert tone="danger">{error}</Alert>}

        <div className="space-y-2">
          <div className="flex gap-2">
            <Button
              variant="secondary"
              onClick={() => saveDraft(clinicalAdvice)}
              disabled={clinicalAdvice === lastSaved.current || draftState === 'saving'}
              title="Saves your private advice; the decision is only stored when signed"
            >
              Save draft
            </Button>
            <Button className="flex-1" disabled={!ready} onClick={() => { cancelPendingSave(); setConfirming(true); }}>
              Review and sign
            </Button>
          </div>
          {!ready && (
            <ul className="space-y-0.5 text-caption font-normal text-neutral-500">
              {missing.map((m) => <li key={m}>· {m}</li>)}
            </ul>
          )}
        </div>

        {/* Not a decision: the examination goes back to the venue for a
            repeat, and the reason is what the desk reads. */}
        {canRetest && <div className="border-t border-neutral-200 pt-3">
          {!retesting ? (
            <button type="button" onClick={() => setRetesting(true)} className="flex items-start gap-1.5 text-left text-caption text-neutral-600 hover:text-neutral-900 max-md:min-h-10 max-md:items-center">
              <ShieldAlert aria-hidden className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              A test needs repeating — send back for a retest
            </button>
          ) : (
            <div className="space-y-2">
              <TextField label="What needs repeating, and why" value={retestReason} onChange={setRetestReason} autoFocus
                placeholder="Audiometry — high ambient noise on the day; repeat in a booth"
                hint="At least ten characters. The venue and the employer's HR read this — name the test and the reason, not a diagnosis." />
              <div className="flex justify-end gap-2">
                <Button size="sm" variant="secondary" onClick={() => setRetesting(false)} disabled={busy}>Back</Button>
                <Button size="sm" variant="danger" loading={busy} disabled={retestReason.trim().length < 10} onClick={async () => {
                  setBusy(true); setError(null);
                  try { await api.post(`/physician/examinations/${orderId}/retest`, { reason: retestReason.trim() }, crypto.randomUUID()); onDone(); }
                  catch (e) { setError(errorMessage(e)); setBusy(false); }
                }}>
                  Send back for retest
                </Button>
              </div>
            </div>
          )}
        </div>}
      </div>

      {confirming && decision && (
        /* Signing is not undoable — a signed review is amended, never edited —
           so it takes two deliberate actions, and the second one restates
           exactly what will be signed and what the employer will see. */
        <Dialog title="Sign this fitness decision" onClose={() => { if (!busy) setConfirming(false); }}>
          <div className="mt-3 space-y-3">
            <p className="text-small text-neutral-700">
              You are signing, in your name, the fitness decision for <strong className="font-semibold text-neutral-900">{worker}</strong>
              {' '}— {examination} examination, on the {resultCount} released result{resultCount === 1 ? '' : 's'} shown on the review.
            </p>
            <DecisionBanner
              decision={decision}
              detail={
                needsRetest && retestDueDate ? `Retest due ${formatDate(retestDueDate)}`
                  : needsRestrictions ? `${restrictionRows.length} restriction${restrictionRows.length === 1 ? '' : 's'}` : undefined
              }
            />
            <div className="rounded-input border border-neutral-200 px-3 py-2 text-small">
              <p className="flex items-center gap-1.5 text-caption text-neutral-700"><Eye aria-hidden className="h-3.5 w-3.5" /> The employer will see</p>
              <ul className="mt-1 space-y-0.5 text-neutral-800">
                <li>{FITNESS_LABEL[decision]}</li>
                {restrictionRows.map((r) => <li key={r.code}>Restriction: {r.label}</li>)}
                {needsRetest && retestDueDate && <li>Retest due {formatDate(retestDueDate)}</li>}
                <li>Next review in {followUpMonths} months</li>
              </ul>
            </div>
            <div className="rounded-input border border-dashed border-neutral-300 bg-neutral-50 px-3 py-2 text-small">
              <p className="flex items-center gap-1.5 text-caption text-neutral-700">
                <Lock aria-hidden className="h-3.5 w-3.5 shrink-0" /> Private clinical record — not shown to the employer
              </p>
              <p className={cn('mt-1 whitespace-pre-line', clinicalAdvice.trim() ? 'text-neutral-800' : 'text-neutral-500')}>
                {clinicalAdvice.trim() || 'No clinical advice recorded.'}
              </p>
            </div>
            <Alert tone="warning" title="Signing is final">
              {ISSUES_CERTIFICATE[decision]
                ? 'A certificate is issued in your name as soon as you sign. The decision cannot be edited afterwards; the certificate can only be revoked.'
                : 'No certificate is issued for this decision. The decision cannot be edited afterwards.'}
            </Alert>
          </div>
          <div className="mt-5 flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setConfirming(false)} disabled={busy}>Back to the review</Button>
            <Button loading={busy} onClick={sign}>
              {ISSUES_CERTIFICATE[decision] ? 'Sign and issue certificate' : 'Sign decision'}
            </Button>
          </div>
        </Dialog>
      )}
    </Card>
  );
}

function capitalise(s: string): string {
  const lower = s.toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}

function age(dob: string | null): string {
  if (!dob) return '';
  const d = new Date(dob);
  const now = new Date();
  let years = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) years--;
  return `${years} years`;
}

/**
 * A laboratory flag's tone. Deliberately not the fitness tones: a critical
 * haemoglobin is not an "unfit" worker, and the four clinical colours are kept
 * for the four decisions so they are never read into anything else. Normal
 * results stay quiet so the ones outside range are what the eye lands on.
 */
function resultTone(flag: string): Tone {
  switch (flag) {
    case 'CRITICAL_LOW': case 'CRITICAL_HIGH': return 'danger';
    case 'LOW': case 'HIGH': case 'ABNORMAL': return 'warning';
    default: return 'neutral';
  }
}

/** A certificate's lifecycle is operational, not a fitness outcome. */
function certificateTone(status: string): Tone {
  return status === 'ACTIVE' ? 'covered' : status === 'REVOKED' ? 'danger' : 'neutral';
}

function rangeText(range: ClinicalResult['referenceRange']): string | null {
  if (!range) return null;
  if (range.label) return `Range ${range.label}`;
  const low = range.low ?? null;
  const high = range.high ?? null;
  if (low !== null && high !== null) return `Range ${low} – ${high}`;
  if (low !== null) return `Range ≥ ${low}`;
  if (high !== null) return `Range ≤ ${high}`;
  return null;
}
